import React, { useState, useEffect, useRef, useCallback, useReducer, useId } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { MapPinIcon, LoaderIcon } from 'lucide-react';
import { Input } from './input';
import { cn } from '@lib/utils';
import { loadGoogleMapsScript, generateGoogleMapsSearchUrl } from '@utils/googleMaps';
import { logger } from '@utils/logger';
import { getGooglePlacesApiKey } from '../../config/api';

/**
 * Narrowed place prediction type — only the fields this component actually uses
 */
type PlacePrediction = Pick<
  google.maps.places.PlacePrediction,
  'placeId' | 'text' | 'mainText' | 'secondaryText' | 'toPlace'
>;

/**
 * Selected location data
 */
export interface LocationData {
  /** Display name for the location */
  displayName: string;
  /**
   * Coordinates as [longitude, latitude] — **LONGITUDE FIRST** (GeoJSON order, matching
   * Google's `lng()` / `lat()` accessors this is built from).
   *
   * Saved on events through `toLocationPayload` (src/utils/eventLocationPayload.ts), which maps
   * the tuple by name. Never spread it positionally: a silent swap stores Lisbon (38.72, -9.14)
   * as (-9.14, 38.72), a point in Sudan that passes every range check.
   */
  coordinates?: [longitude: number, latitude: number];
  /** Google place id of the selected suggestion; absent for typed addresses. */
  placeId?: string;
  /** Auto-generated Google Maps URL */
  googleMapsUrl?: string;
}

function createLocationData(
  displayName: string,
  coordinates?: [longitude: number, latitude: number],
  placeId?: string,
): LocationData {
  return {
    displayName,
    coordinates,
    placeId,
    googleMapsUrl: generateGoogleMapsSearchUrl(displayName),
  };
}

interface LocationInputProps {
  value?: LocationData;
  onChange?: (location: LocationData) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Optional place type filter (e.g., ['locality', 'administrative_area_level_1'] for cities) */
  placeTypes?: string[];
}

// --- Autocomplete state reducer (groups predictions, loading, suggestions, selection) ---

interface AutocompleteState {
  predictions: PlacePrediction[];
  isLoading: boolean;
  showSuggestions: boolean;
  selectedIndex: number;
  fetchError: boolean;
}

/** CLEAR: resets predictions/loading (user cleared input or input too short)
 *  FETCH_ERROR: API call failed (shows user-facing error message)
 *  RESET: full state reset (after selection or escape) */
type AutocompleteAction =
  | { type: 'START_LOADING' }
  | { type: 'SET_PREDICTIONS'; predictions: PlacePrediction[] }
  | { type: 'CLEAR' }
  | { type: 'FETCH_ERROR' }
  | { type: 'SET_SELECTED_INDEX'; index: number }
  | { type: 'MOVE_SELECTION'; direction: 'up' | 'down' }
  | { type: 'SHOW' }
  | { type: 'HIDE' }
  | { type: 'RESET' };

const initialAutocompleteState: AutocompleteState = {
  predictions: [],
  isLoading: false,
  showSuggestions: false,
  selectedIndex: -1,
  fetchError: false,
};

const autocompleteReducer = (
  state: AutocompleteState,
  action: AutocompleteAction,
): AutocompleteState => {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, isLoading: true, fetchError: false };
    case 'SET_PREDICTIONS':
      return {
        ...state,
        predictions: action.predictions,
        isLoading: false,
        fetchError: false,
        selectedIndex: -1,
      };
    case 'CLEAR':
      return { ...state, predictions: [], isLoading: false, fetchError: false, selectedIndex: -1 };
    case 'FETCH_ERROR':
      return { ...state, predictions: [], isLoading: false, fetchError: true, selectedIndex: -1 };
    case 'SET_SELECTED_INDEX':
      return {
        ...state,
        selectedIndex: Math.min(Math.max(-1, action.index), state.predictions.length - 1),
      };
    case 'MOVE_SELECTION':
      if (action.direction === 'down') {
        return {
          ...state,
          selectedIndex:
            state.selectedIndex < state.predictions.length - 1
              ? state.selectedIndex + 1
              : state.selectedIndex,
        };
      }
      return {
        ...state,
        selectedIndex: state.selectedIndex > 0 ? state.selectedIndex - 1 : -1,
      };
    case 'SHOW':
      return { ...state, showSuggestions: true };
    case 'HIDE':
      return { ...state, showSuggestions: false };
    case 'RESET':
      return initialAutocompleteState;
  }
};

// --- Suggestions dropdown subcomponent ---

interface SuggestionsDropdownProps {
  predictions: PlacePrediction[];
  selectedIndex: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
  listboxId: string;
  onSelect: (prediction: PlacePrediction) => void;
  onHover: (index: number) => void;
}

const SuggestionsDropdown: React.FC<SuggestionsDropdownProps> = ({
  predictions,
  selectedIndex,
  containerRef,
  listboxId,
  onSelect,
  onHover,
}) => (
  <div
    ref={containerRef}
    id={listboxId}
    role="listbox"
    className="absolute z-overlay w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
  >
    {predictions.map((prediction, index) => (
      <button
        key={prediction.placeId}
        id={`${listboxId}-option-${index}`}
        role="option"
        aria-selected={selectedIndex === index}
        type="button"
        className={cn(
          'w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg',
          selectedIndex === index && 'bg-gray-50',
        )}
        onClick={() => {
          onSelect(prediction);
        }}
        onMouseEnter={() => {
          onHover(index);
        }}
      >
        <div className="flex items-start gap-2">
          <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {prediction.mainText?.text ?? prediction.text?.text ?? ''}
            </div>
            {prediction.secondaryText?.text && (
              <div className="text-xs text-gray-500 truncate">{prediction.secondaryText.text}</div>
            )}
          </div>
        </div>
      </button>
    ))}
  </div>
);

// --- Main component ---

/**
 * Location input component with autocomplete suggestions using Google Places API
 *
 * Features:
 * - Google Places AutocompleteSuggestion for location suggestions
 * - Selected location caching to avoid redundant Place Details fetches (50 entries, LRU)
 * - Optional place type filtering for different use cases
 * - Two-mode UI: edit mode (input) and display mode (formatted display)
 * - Manual location input always generates Google Maps URLs
 */
export const LocationInput: React.FC<LocationInputProps> = ({
  value,
  onChange,
  placeholder = 'Choose Location',
  disabled = false,
  className,
  placeTypes,
}) => {
  const listboxId = useId();
  const [editingValue, setEditingValue] = useState('');
  const [autocomplete, dispatch] = useReducer(autocompleteReducer, initialAutocompleteState);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  // Derive input value: use editingValue when in edit mode, otherwise use prop value
  const inputValue = isEditMode ? editingValue : value?.displayName || '';

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Google Places API (New) - class references and session token
  const AutocompleteSuggestionRef = useRef<typeof google.maps.places.AutocompleteSuggestion | null>(
    null,
  );
  const AutocompleteSessionTokenRef = useRef<
    typeof google.maps.places.AutocompleteSessionToken | null
  >(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Cache for storing previously selected locations (50 entries max, LRU)
  // Uses placeId as key to ensure uniqueness (displayName can have collisions)
  const cacheRef = useRef<Map<string, LocationData>>(new Map());

  // Track accepted value to prevent unwanted requests
  const acceptedValueRef = useRef<string>('');

  // Track mounted state to prevent updates after unmount
  const isMountedRef = useRef(true);

  // Guard against stale async responses when user types faster than API responds
  const latestInputRef = useRef('');

  // Prevent double-clicks from firing parallel Place Details requests
  const isSelectingRef = useRef(false);

  /**
   * Extract primary name from display name (text before first comma)
   */
  const extractPrimaryName = (displayName: string): string => {
    const parts = displayName.split(',');
    return parts[0]?.trim() || displayName;
  };

  /**
   * Extract full address from display name (text after first comma)
   */
  const extractFullAddress = (displayName: string): string => {
    const parts = displayName.split(',');
    return parts.slice(1).join(',').trim();
  };

  /**
   * Safely renew the session token after a fetchFields call concludes a session
   */
  const renewSessionToken = () => {
    try {
      if (AutocompleteSessionTokenRef.current) {
        sessionTokenRef.current = new AutocompleteSessionTokenRef.current();
      } else {
        logger.error('Cannot renew session token: AutocompleteSessionToken class unavailable');
        sessionTokenRef.current = null;
      }
    } catch (error) {
      logger.error('Failed to renew session token:', error);
      sessionTokenRef.current = null;
    }
  };

  /**
   * Initialize Google Places API (New) classes
   */
  useEffect(() => {
    const apiKey = getGooglePlacesApiKey();
    if (!apiKey) {
      logger.error('Google Places API key not found');
      setInitError('Location suggestions unavailable');
      setIsInitializing(false);
      return;
    }

    const cancellationToken: { cancelled: boolean } = { cancelled: false };

    const initializePlaces = async () => {
      if (cancellationToken.cancelled) return;

      try {
        // Load Google Maps script
        await loadGoogleMapsScript(apiKey);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure mutation from cleanup
        if (cancellationToken.cancelled) return;

        // Load Places library (New API classes)
        // tsc disagrees with eslint here: under tsconfig.app.json
        // `importLibrary('places')` resolves to the full library union, so
        // dropping this assertion fails type-check on AutocompleteSuggestion /
        // AutocompleteSessionToken. The rule is auto-fixable, so without the
        // directive below `lint:fix` would silently break the build.
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- see above
          (await window.google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure mutation from cleanup
        if (cancellationToken.cancelled) return;

        // Store class references for use in callbacks
        AutocompleteSuggestionRef.current = AutocompleteSuggestion;
        AutocompleteSessionTokenRef.current = AutocompleteSessionToken;

        // Create initial session token for billing optimization
        sessionTokenRef.current = new AutocompleteSessionToken();

        setIsInitializing(false);
      } catch (error) {
        logger.error('Failed to initialize Google Places:', error);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure mutation from cleanup
        if (!cancellationToken.cancelled) {
          setInitError('Location suggestions unavailable');
          setIsInitializing(false);
        }
      }
    };

    void initializePlaces();

    return () => {
      cancellationToken.cancelled = true;
    };
  }, []);

  /**
   * Focus input when entering edit mode (accessible alternative to autoFocus)
   */
  useEffect(() => {
    if (isEditMode) {
      inputRef.current?.focus();
    }
  }, [isEditMode]);

  /**
   * Fetch place predictions from AutocompleteSuggestion (New API)
   */
  const fetchPredictions = useCallback(
    async (input: string) => {
      if (!AutocompleteSuggestionRef.current || !input || input.length < 2) {
        if (isMountedRef.current) {
          dispatch({ type: 'CLEAR' });
        }
        return;
      }

      try {
        const request: google.maps.places.AutocompleteRequest = {
          input,
          sessionToken: sessionTokenRef.current ?? undefined,
        };

        // Map placeTypes to includedPrimaryTypes (new API field name)
        if (placeTypes && placeTypes.length > 0) {
          request.includedPrimaryTypes = placeTypes;
        }

        const { suggestions } =
          await AutocompleteSuggestionRef.current.fetchAutocompleteSuggestions(request);

        if (!isMountedRef.current) return;

        // Discard stale response if user has typed something different since this request fired
        if (input !== latestInputRef.current) return;

        // Extract placePrediction from each suggestion (cast to narrowed type)
        const placePredictions: PlacePrediction[] = suggestions
          .map(s => s.placePrediction)
          .filter((p): p is google.maps.places.PlacePrediction => p != null);

        dispatch({ type: 'SET_PREDICTIONS', predictions: placePredictions });
      } catch (error) {
        logger.error('Places Autocomplete error:', error);
        if (isMountedRef.current) {
          dispatch({ type: 'FETCH_ERROR' });
        }
      }
    },
    [placeTypes],
  );

  // Debounced prediction fetcher — only the API call is debounced, not the UI state
  const debouncedFetchPredictions = useDebouncedCallback((value: string) => {
    void fetchPredictions(value);
  }, 500);

  /**
   * Handle input change with debouncing
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    latestInputRef.current = newValue;
    setEditingValue(newValue);
    dispatch({ type: 'SHOW' });
    dispatch({ type: 'SET_SELECTED_INDEX', index: -1 });

    // Clear accepted value if user types something different
    if (newValue !== acceptedValueRef.current) {
      acceptedValueRef.current = '';
    }

    // Don't fetch if input matches accepted value
    if (newValue === acceptedValueRef.current) {
      dispatch({ type: 'CLEAR' });
      return;
    }

    if (newValue.trim().length < 2) {
      debouncedFetchPredictions.cancel();
      dispatch({ type: 'CLEAR' });
      return;
    }

    dispatch({ type: 'START_LOADING' });
    debouncedFetchPredictions(newValue);
  };

  /**
   * Get place details using new Place class and create LocationData
   */
  const getPlaceDetails = useCallback(
    async (prediction: PlacePrediction, displayName: string): Promise<LocationData> => {
      const place = prediction.toPlace();

      // Session token was associated at autocomplete request time; fetchFields concludes the billing session
      const { place: fetchedPlace } = await place.fetchFields({
        fields: ['location'],
      });

      return createLocationData(
        displayName,
        fetchedPlace.location
          ? [fetchedPlace.location.lng(), fetchedPlace.location.lat()]
          : undefined,
        prediction.placeId,
      );
    },
    [],
  );

  /**
   * Handle suggestion selection
   */
  const selectSuggestion = async (prediction: PlacePrediction) => {
    if (isSelectingRef.current) return;

    const displayName = prediction.text?.text;
    const placeId = prediction.placeId;

    if (!displayName || !placeId) {
      logger.error('Prediction missing required fields', { displayName, placeId });
      dispatch({ type: 'RESET' });
      setIsEditMode(false);
      return;
    }

    // Check cache first using placeId for uniqueness
    const cached = cacheRef.current.get(placeId);
    if (cached) {
      acceptedValueRef.current = cached.displayName;
      dispatch({ type: 'RESET' });
      setIsEditMode(false);
      onChange?.(cached);
      return;
    }

    isSelectingRef.current = true;
    try {
      dispatch({ type: 'START_LOADING' });

      const locationData = await getPlaceDetails(prediction, displayName);
      renewSessionToken();

      if (!isMountedRef.current) return;

      // Store in cache (LRU: remove oldest if exceeds 50)
      if (cacheRef.current.size >= 50) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey) {
          cacheRef.current.delete(firstKey);
        }
      }
      cacheRef.current.set(placeId, locationData);

      acceptedValueRef.current = locationData.displayName;
      onChange?.(locationData);
    } catch (error) {
      logger.error('Failed to get place details, using location without coordinates:', error);
      if (!isMountedRef.current) return;

      // No pin (so no timezone lookup), but the place id is still the host's choice.
      const fallback = createLocationData(displayName, undefined, placeId);
      acceptedValueRef.current = displayName;
      onChange?.(fallback);
    } finally {
      isSelectingRef.current = false;
      if (isMountedRef.current) {
        dispatch({ type: 'RESET' });
        setIsEditMode(false);
      }
    }
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!autocomplete.showSuggestions || autocomplete.predictions.length === 0) {
      // Handle Enter/Escape for mode switching
      if (e.key === 'Enter') {
        e.preventDefault();
        if (inputValue.trim()) {
          const manualLocation = createLocationData(inputValue.trim());
          acceptedValueRef.current = manualLocation.displayName;
          setIsEditMode(false);
          onChange?.(manualLocation);
        }
      } else if (e.key === 'Escape') {
        setIsEditMode(false);
        dispatch({ type: 'HIDE' });
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        dispatch({ type: 'MOVE_SELECTION', direction: 'down' });
        break;
      case 'ArrowUp':
        e.preventDefault();
        dispatch({ type: 'MOVE_SELECTION', direction: 'up' });
        break;
      case 'Enter':
        e.preventDefault();
        if (
          autocomplete.selectedIndex >= 0 &&
          autocomplete.predictions[autocomplete.selectedIndex]
        ) {
          void selectSuggestion(autocomplete.predictions[autocomplete.selectedIndex]);
        } else if (inputValue.trim()) {
          const manualLocation = createLocationData(inputValue.trim());
          acceptedValueRef.current = manualLocation.displayName;
          dispatch({ type: 'RESET' });
          setIsEditMode(false);
          onChange?.(manualLocation);
        }
        break;
      case 'Escape':
        dispatch({ type: 'RESET' });
        setIsEditMode(false);
        break;
    }
  };

  /**
   * Handle clicks outside to close suggestions
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        dispatch({ type: 'HIDE' });

        // If clicking outside while editing, revert to display mode
        if (isEditMode) {
          setIsEditMode(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditMode]);

  /**
   * Cleanup effect
   */
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      debouncedFetchPredictions.cancel();
    };
  }, [debouncedFetchPredictions]);

  // Get display values for non-edit mode
  const primaryName = value?.displayName ? extractPrimaryName(value.displayName) : '';
  const fullAddress = value?.displayName ? extractFullAddress(value.displayName) : '';
  const hasValidLocation = Boolean(value?.displayName);

  return (
    <div className={cn('relative w-full', className)}>
      {/* Edit mode - shows input with suggestions */}
      {isEditMode ? (
        <div className="relative">
          <div className="relative">
            <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={autocomplete.showSuggestions && autocomplete.predictions.length > 0}
              aria-controls={listboxId}
              aria-activedescendant={
                autocomplete.selectedIndex >= 0
                  ? `${listboxId}-option-${autocomplete.selectedIndex}`
                  : undefined
              }
              aria-autocomplete="list"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                dispatch({ type: 'SHOW' });
              }}
              placeholder={placeholder}
              disabled={disabled || isInitializing}
              className="pl-9 pr-9"
            />
            {autocomplete.isLoading && (
              <LoaderIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
            )}
          </div>

          {/* Suggestions dropdown */}
          {autocomplete.showSuggestions && autocomplete.predictions.length > 0 && (
            <SuggestionsDropdown
              predictions={autocomplete.predictions}
              selectedIndex={autocomplete.selectedIndex}
              containerRef={suggestionsRef}
              listboxId={listboxId}
              onSelect={prediction => void selectSuggestion(prediction)}
              onHover={index => {
                dispatch({ type: 'SET_SELECTED_INDEX', index });
              }}
            />
          )}

          {autocomplete.fetchError && (
            <p className="text-xs text-amber-600 mt-1">
              Could not load suggestions. Try typing your location manually.
            </p>
          )}

          {initError && <p className="text-xs text-amber-600 mt-1">{initError}</p>}
        </div>
      ) : (
        /* Display mode - shows formatted location with click to edit */
        <button
          type="button"
          onClick={() => {
            if (!disabled && !isInitializing) {
              setEditingValue(value?.displayName || '');
              setIsEditMode(true);
              dispatch({ type: 'SHOW' });
            }
          }}
          disabled={disabled || isInitializing}
          className={cn(
            'w-full px-3 py-2 rounded-md bg-white text-left transition-colors',
            'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            !hasValidLocation && 'text-gray-400',
          )}
        >
          <div className="flex items-start gap-2">
            <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
            {hasValidLocation ? (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{primaryName}</div>
                {fullAddress && <div className="text-xs text-gray-500 truncate">{fullAddress}</div>}
              </div>
            ) : (
              <div className="text-sm">{placeholder}</div>
            )}
          </div>
        </button>
      )}
    </div>
  );
};
